import React from "react";
import PageWithHeader from "../common/PageWithHeader";
import { useGetPropertyByIdQuery } from "../../redux/residentPortalApi";
import {
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  Typography,
  useTheme,
  useMediaQuery,
  Chip,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { makeStyles } from "@mui/styles";
import { theme } from "../../theme/theme";
import {
  developmentInfo,
  formatPropertyNumber,
  formatPropertyType,
} from "../../utils/propertyUtils";
import HomeIcon from "@mui/icons-material/Home";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import LayersIcon from "@mui/icons-material/Layers";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import DescriptionIcon from "@mui/icons-material/Description";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import BuildIcon from "@mui/icons-material/Build";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import NoticeTabContent from "../common/PropertyTabs/NoticeTabContent";
import UtilitiesTabContent from "../common/PropertyTabs/UtilitiesTabContent";
import DocumentTabContent from "../common/PropertyTabs/DocumentTabContent";
import VisitsTabContent from "../common/PropertyTabs/VisitsTabContent";
import PadelCourtTab from "../padel/PadelCourtTab";
import MaintenanceTab from "../maintenance/MaintenanceTab";
import Unauthorised from "../common/Unauthorised";
import LoadingCover from "../common/LoadingCover";
import { useParams } from "react-router-dom";

const propertyTabs = [
  {
    label: 'Utilities',
    icon: ElectricBoltIcon,
  },
  {
    label: 'Padel Court',
    icon: SportsTennisIcon,
  },
  {
    label: 'Maintenance',
    icon: BuildIcon,
  },
  {
    label: 'Visits',
    icon: FactCheckIcon,
  },
  {
    label: 'Notices',
    icon: NotificationsIcon,
  },
  {
    label: 'Documents',
    icon: DescriptionIcon,
  },
];

const useStyles = makeStyles({
  root: {
    padding: theme.spacing(4),
    backgroundColor: "#f8f9fa",
    minHeight: "calc(100vh - 90px)",
  },
  container: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(8),
  },
  heroImage: (props: any) => ({
    width: "100%",
    height: "300px",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5)), url(${props.bannerUrl})`,
    position: "relative",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  }),
  heroContent: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    padding: theme.spacing(6),
    background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
  },
  developmentTitle: {
    color: "#fff",
    fontWeight: 700,
    fontSize: "2.5rem",
    marginBottom: theme.spacing(1),
    textShadow: "0 2px 4px rgba(0,0,0,0.3)",
  },
  propertyTitle: {
    color: "#fff",
    fontWeight: 500,
    fontSize: "1.5rem",
    marginBottom: theme.spacing(2),
    textShadow: "0 2px 4px rgba(0,0,0,0.3)",
  },
  propertyChip: {
    backgroundColor: "#3498db",
    color: "white",
    fontWeight: 600,
    fontSize: "0.875rem",
    marginRight: theme.spacing(2),
  },
  tabsContainer: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    marginTop: theme.spacing(6),
    overflow: "hidden",
  },
  tabs: {
    borderBottom: "1px solid #eaeaea",
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 600,
      fontSize: "1rem",
      minHeight: "60px",
    },
    "& .Mui-selected": {
      color: "#3498db",
    },
    "& .MuiTabs-indicator": {
      backgroundColor: "#3498db",
      height: "3px",
    },
  },
  tabIcon: {
    marginRight: theme.spacing(1),
  },
  tabContent: {
    padding: theme.spacing(4),
  },
  propertyInfoCard: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    marginTop: theme.spacing(6),
    overflow: "hidden",
  },
  propertyInfoHeader: {
    padding: theme.spacing(4),
    borderBottom: "1px solid #eaeaea",
  },
  propertyInfoContent: {
    padding: theme.spacing(4),
  },
  propertyInfoTitle: {
    fontWeight: 600,
    color: "#2c3e50",
    marginBottom: theme.spacing(2),
  },
  propertyText: {
    fontSize: "1rem",
    display: "flex",
    alignItems: "center",
    color: "#7f8c8d",
    marginBottom: theme.spacing(3),
  },
  icon: {
    marginRight: theme.spacing(2),
    color: "#3498db",
  },
});

const PropertyPage: React.FC = () => {
  const params = useParams();
  const propertyId = params?.uuid ?? "";
  const {
    data: property,
    isLoading,
    error,
  } = useGetPropertyByIdQuery({ id: propertyId }, { skip: !propertyId });

  const bannerUrl =
    property &&
    developmentInfo(property.development, property.phase, property.type).banner;
  const classes = useStyles({ bannerUrl });
  const muiTheme = useTheme();
  const isTabletOrMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const [value, setValue] = React.useState(0);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleSelectTab = (event: SelectChangeEvent<number>) => {
    setValue(Number(event.target.value));
  };

  if (error && "status" in error && error.status === 401) {
    return <Unauthorised />;
  }

  if (isLoading) {
    return <LoadingCover />;
  }

  if (!property) {
    return (
      <PageWithHeader>
        <Helmet>
          <title>Property Not Found | WestProp Residents Portal</title>
          <meta
            name="description"
            content="The property you are looking for could not be found."
          />
        </Helmet>
        <Container
          maxWidth="sm"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "90vh",
            textAlign: "center",
          }}
        >
          <Paper elevation={2} sx={{ padding: 20, borderRadius: "30px" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <SearchOffIcon sx={{ fontSize: 100, color: "#3498db" }} />
              <Typography variant="h2" sx={{ mt: 2 }}>
                Property Not Found
              </Typography>
              <Typography variant="h6" sx={{ mt: 2 }}>
                The property you are looking for could not be found.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                sx={{ mt: 4 }}
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </Box>
          </Paper>
        </Container>
      </PageWithHeader>
    );
  }

  return (
    <PageWithHeader>
      <Helmet>
        <title>
          {property.development
            ? `${formatPropertyNumber(property)} | ${
                developmentInfo(
                  property.development,
                  property.phase,
                  property.type,
                ).name
              }`
            : "Property Details"}{" "}
          | WestProp Residents Portal
        </title>
        <meta
          name="description"
          content={`View details for ${formatPropertyNumber(property)} in ${
            property.development
              ? developmentInfo(
                  property.development,
                  property.phase,
                  property.type,
                ).name
              : "WestProp development"
          }`}
        />
      </Helmet>
      <Box className={classes.root}>
        <Container maxWidth="xl" className={classes.container}>
          {/* Hero Section */}
          <Box className={classes.heroImage}>
            {property && property.development && (
              <Box className={classes.heroContent}>
                <Typography className={classes.developmentTitle}>
                  {
                    developmentInfo(
                      property.development,
                      property.phase,
                      property.type,
                    ).name
                  }
                </Typography>
                <Typography className={classes.propertyTitle}>
                  {formatPropertyNumber(property)}
                </Typography>
                <Box display="flex" alignItems="center">
                  {property?.type && (
                    <Chip
                      label={formatPropertyType(property?.type ?? "")}
                      size="medium"
                      className={classes.propertyChip}
                    />
                  )}
                  {property?.unitArea && (
                    <Chip
                      label={`${property?.unitArea}m²`}
                      size="medium"
                      className={classes.propertyChip}
                      icon={<SquareFootIcon color="inherit" />}
                    />
                  )}
                </Box>
              </Box>
            )}
          </Box>

          <Grid container spacing={4}>
            {/* Property Info Card */}
            <Grid item xs={12} md={4}>
              <Paper className={classes.propertyInfoCard} elevation={0}>
                <Box className={classes.propertyInfoHeader}>
                  <Typography
                    variant="h5"
                    className={classes.propertyInfoTitle}
                  >
                    Property Details
                  </Typography>
                </Box>
                <Box className={classes.propertyInfoContent}>
                  {property?.type && (
                    <Typography className={classes.propertyText}>
                      <HomeIcon className={classes.icon} />
                      {formatPropertyType(property?.type ?? "")}
                    </Typography>
                  )}
                  {property?.unitArea && (
                    <Typography className={classes.propertyText}>
                      <SquareFootIcon className={classes.icon} />
                      {property?.unitArea}m² Area
                    </Typography>
                  )}
                  {!!property && (
                    <Typography className={classes.propertyText}>
                      <LayersIcon className={classes.icon} />
                      {formatPropertyNumber(property)}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Tabs Section */}
            <Grid item xs={12} md={8}>
              <Paper className={classes.tabsContainer} elevation={0}>
                {isTabletOrMobile ? (
                  <Box sx={{ p: 2, borderBottom: '1px solid #eaeaea' }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="property-section-label">Section</InputLabel>
                      <Select<number>
                        labelId="property-section-label"
                        value={value}
                        label="Section"
                        onChange={handleSelectTab}
                      >
                        {propertyTabs.map((tab, index) => (
                          <MenuItem key={tab.label} value={index}>{tab.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ) : (
                  <Tabs
                    value={value}
                    onChange={handleChange}
                    className={classes.tabs}
                    variant="standard"
                  >
                    {propertyTabs.map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <Tab
                          key={tab.label}
                          icon={<Icon className={classes.tabIcon} />}
                          iconPosition="start"
                          label={tab.label}
                        />
                      );
                    })}
                  </Tabs>
                )}
                <Box className={classes.tabContent}>
                  {value === 0 && property && <UtilitiesTabContent property={property} />}
                  {value === 1 && property && <PadelCourtTab property={property} />}
                  {value === 2 && property && <MaintenanceTab property={property} />}
                  {value === 3 && property && <VisitsTabContent propertyId={property.id ?? ''} />}
                  {value === 4 && property && <NoticeTabContent propertyId={property.id ?? ''} development={property?.development} />}
                  {value === 5 && property && <DocumentTabContent />}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </PageWithHeader>
  );
};

export default PropertyPage;
